"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import PaymentModal from "@/components/molecules/payment-section/payment-modal/payment-modal";
import { QuoteReview } from "@/config/app-constant";
import { useGetPaymentTitle } from "@/hooks/api/payment/magnolia-api";
import useGtmPaymentFaildOptions from "@/hooks/gtm/payment/use-gtm-payment-failed-options";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useUpdateProgressBar from "@/hooks/progress/use-update-progress-bar";
import { RootState } from "@/store/root-reducer";
import paymentFailedDataMap from "@/utils/gtm/payment-failed-data-map";

function PaymentMessages() {
  const router = useRouter();

  const { referenceId, attributes } = useSelector(
    (state: RootState) => state.quote
  );
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  const pclError = searchParams?.get("pclError");
  const pclCancel = searchParams?.get("pclCancel");
  const paymentError = searchParams?.get("paymentError");
  const paymentData = useGetPaymentTitle();
  const isCheck = !!(
    ((pclCancel && pclCancel.trim() !== "") ||
      (pclError && pclError.trim() !== "") ||
      (paymentError && paymentError.trim() !== "")) &&
    referenceId
  );
  const { pageReferrer, pageUrl, pageTitle } =
    useCustomPageTracking(QuoteReview);

  const { current } = useWizard();
  useUpdateProgressBar(current?.progressBarPercentage ?? -1);

  const errorType = useMemo(() => {
    if (pclError) return "pclError";
    if (pclCancel) return "pclCancel";
    if (paymentError) return "paymentError";
    return "paymentError";
  }, [pclCancel, pclError, paymentError]);

  const ModalTitle = useMemo(() => {
    switch (errorType) {
      case "paymentError":
        return paymentData?.errorPopModalTitle;
      case "pclError":
        return paymentData?.errorPopModalTitlePcl;
      case "pclCancel":
        return paymentData?.cancelPopModalTitle;
      default:
        return "Error";
    }
  }, [errorType, paymentData]);

  useEffect(() => {
    if (pclCancel || pclError || paymentError) {
      setIsOpen(true);
    }
  }, [pclCancel, pclError, paymentError]);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  // GTM Trigger
  let gtmPaymentFaild = null;
  if (referenceId) {
    gtmPaymentFaild = paymentFailedDataMap(
      attributes,
      ModalTitle ?? "",
      !!attributes
    );
  }

  useGtmPaymentFaildOptions({
    paymentFaildData: gtmPaymentFaild,
    isReady: isCheck,
  });

  return (
    <PaymentModal
      data-testid="review-payment-modal"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      type={errorType}
      router={router}
    />
  );
}

export default PaymentMessages;
